import { useCallback, useMemo, type ReactElement } from "react";
import { Pin, PinOff, StickyNote } from "lucide-react";
import { color, radius, space } from "@tokens/index";
import { IconButton, Text } from "../../primitives";
import type { WorkItem } from "../../../experience-shell/workflows";
import { formatRelativeTime } from "./formatters";
import { InsertedRow } from "./InsertedRow";
import { useNewRowDetector } from "./useNewRowDetector";
import { NoteComposerRow, NoteComposerTrigger } from "./NoteComposer";
import { useNoteComposer } from "./useNoteComposer";

/**
 * Notes attached to one task, pinned first, with an inline composer that
 * links the new note to the task. The section takes its content height up
 * to 45% of the panel and scrolls past that; the thread section below owns
 * the rest. Read-only list: editing is NotePanel's job.
 */
export function TaskNotesSection({
  taskId,
  notes,
  onAddNote,
  isAddingNote,
  onPinNote,
  isPinningId,
}: {
  readonly taskId: string;
  readonly notes: readonly WorkItem[];
  readonly onAddNote: (body: string, parentWorkItemId?: string) => void;
  readonly isAddingNote: boolean;
  readonly onPinNote: (noteId: string, pinned: boolean) => void;
  readonly isPinningId: string | null;
}): ReactElement {
  const save = useCallback((body: string) => onAddNote(body, taskId), [onAddNote, taskId]);
  const composer = useNoteComposer(save);

  const ordered = useMemo<WorkItem[]>(() => {
    const own = notes.filter((n) => n.parentWorkItemId === taskId);
    return [...own.filter((n) => n.pinned), ...own.filter((n) => !n.pinned)];
  }, [notes, taskId]);
  const isNewRow = useNewRowDetector(ordered.map((n) => n.id));

  return (
    <section
      aria-label="Task notes"
      data-testid="task-notes"
      style={{
        flex: "0 1 auto",
        maxHeight: "45%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: space[2],
          height: 40,
          flexShrink: 0,
          padding: `0 ${space[3]} 0 ${space[4]}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: space[2], minWidth: 0 }}>
          <StickyNote size={14} strokeWidth={2.25} color={color.note.accent} aria-hidden="true" />
          <Text role="caps" size="xs" tone="muted" truncate>
            Notes{ordered.length > 0 ? ` · ${String(ordered.length)}` : ""}
          </Text>
        </div>
        <NoteComposerTrigger composer={composer} />
      </div>

      <NoteComposerRow
        composer={composer}
        placeholder="Capture a note on this task…"
        disabled={isAddingNote}
      />

      {ordered.length > 0 && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: `0 ${space[4]} ${space[2]}`,
            overflowY: "auto",
          }}
        >
          {ordered.map((note) => (
            <InsertedRow key={note.id} isNew={isNewRow(note.id)} gap={6}>
              <TaskNoteRow note={note} onPinNote={onPinNote} isPinning={isPinningId === note.id} />
            </InsertedRow>
          ))}
        </div>
      )}
    </section>
  );
}

function TaskNoteRow({
  note,
  onPinNote,
  isPinning,
}: {
  readonly note: WorkItem;
  readonly onPinNote: (noteId: string, pinned: boolean) => void;
  readonly isPinning: boolean;
}): ReactElement {
  const author = note.createdByDisplayName ?? note.createdBy ?? "Unknown";
  return (
    <div
      data-testid="task-note-row"
      style={{
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        gap: space[2],
        padding: `${space[2]} ${space[2]} ${space[2]} ${space[3]}`,
        borderRadius: radius.md,
        background: color.bg.surface2,
        minWidth: 0,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          top: space[2],
          bottom: space[2],
          width: 2,
          borderRadius: 1,
          background: color.note.accent,
        }}
      />
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1, gap: 2 }}>
        <Text role="body" size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {note.body}
        </Text>
        <Text role="caption" size="xs" tone="subtle" truncate>
          {note.pinned ? "Pinned · " : ""}
          {author} · {formatRelativeTime(note.createdAt)}
        </Text>
      </span>
      <IconButton
        icon={note.pinned ? PinOff : Pin}
        size="sm"
        variant={note.pinned ? "surface" : "ghost"}
        disabled={isPinning}
        aria-label={note.pinned ? "Unpin note" : "Pin note"}
        aria-pressed={note.pinned}
        onClick={() => onPinNote(note.id, !note.pinned)}
      />
    </div>
  );
}
